// Single source of truth for semantic status meaning. Colour NEVER carries meaning alone:
// every state resolves to a label + an icon as well as a tint.
import { CheckCircle2, PlayCircle, Clock, AlertTriangle, Lock, XCircle, ShieldCheck, Sparkles, FileEdit, Upload, Archive, Pause, HelpCircle } from "lucide-react";

const T = {
  done: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, hex: "#059669" },
  active: { cls: "bg-sky-50 text-sky-700 border-sky-200", icon: PlayCircle, hex: "#0284C7" },
  warn: { cls: "bg-amber-50 text-amber-800 border-amber-200", icon: Clock, hex: "#B45309" },
  danger: { cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle, hex: "#DC2626" },
  locked: { cls: "bg-amber-100 text-amber-900 border-amber-300", icon: Lock, hex: "#92400E" },
  credit: { cls: "bg-purple-50 text-purple-700 border-purple-200", icon: ShieldCheck, hex: "#6D28D9" },
  optional: { cls: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: Sparkles, hex: "#4338CA" },
  neutral: { cls: "bg-slate-50 text-slate-600 border-slate-200", icon: null, hex: "#94A3B8" },
  muted: { cls: "bg-slate-100 text-slate-500 border-slate-200", icon: Archive, hex: "#64748B" },
};

const def = (tone, label, icon) => ({ label, cls: T[tone].cls, icon: icon === undefined ? T[tone].icon : icon, hex: T[tone].hex });

// The eleven states the product must express, plus the operational states.
export const STATUS = {
  // --- learner / compliance -------------------------------------------------
  completed: def("done", "Completed"),
  complete: def("done", "Completed"),
  compliant: def("done", "Compliant"),
  passed: def("done", "Passed"),
  in_progress: def("active", "In Progress"),
  not_started: def("neutral", "Not Started", HelpCircle),
  due_soon: def("warn", "Due Soon"),
  overdue: def("danger", "Overdue"),
  locked: def("locked", "Locked"),
  failed: def("danger", "Failed", XCircle),
  exempt: def("credit", "Exempt"),
  waived: def("credit", "Waived"),
  satisfied_by_equivalency: def("credit", "Credit Given"),
  assigned: def("active", "Required", ShieldCheck),
  required: def("active", "Required", ShieldCheck),
  elective: def("optional", "Optional"),
  optional: def("optional", "Optional"),
  // --- content lifecycle ----------------------------------------------------
  draft: def("neutral", "Draft", FileEdit),
  review: def("warn", "In Review"),
  published: def("done", "Published", Upload),
  archived: def("muted", "Archived"),
  preview: def("neutral", "Preview", HelpCircle),
  // --- operational ----------------------------------------------------------
  active: def("done", "Active"),
  pending: def("warn", "Pending"),
  approved: def("done", "Approved"),
  rejected: def("danger", "Rejected"),
  delivered: def("done", "Delivered"),
  sent: def("done", "Sent"),
  skipped: def("neutral", "Skipped", null),
  retrying: def("warn", "Retrying"),
  queued: def("neutral", "Queued", Clock),
  running: def("active", "Running"),
  scanning: def("active", "Scanning"),
  downloading: def("active", "Downloading"),
  importing: def("active", "Importing"),
  validating: def("active", "Validating"),
  complete_with_warnings: def("warn", "Imported · needs review", AlertTriangle),
  content_pending: def("warn", "Content pending"),
  manual_review: def("warn", "Needs attention", AlertTriangle),
  paused: def("warn", "Paused", Pause),
  cancelled: def("muted", "Cancelled", XCircle),
  connected: def("done", "Connected"),
  disconnected: def("danger", "Disconnected"),
  matched: def("done", "Matched"),
  unmatched: def("danger", "Unmatched", XCircle),
  ignored: def("muted", "Ignored"),
  unresolved: def("warn", "Unresolved"),
  resolved: def("done", "Resolved"),
  fully_compatible: def("done", "Fully compatible"),
  partially_compatible: def("warn", "Partially compatible"),
  maintenance: def("neutral", "Maintenance", null),
  unknown: def("neutral", "Unknown", HelpCircle),
  critical: def("danger", "Critical"),
  manual_work: def("warn", "Manual work"),
  configuration: def("active", "Configuration", HelpCircle),
  warning: def("warn", "Warning"),
  informational: def("neutral", "For information", null),
};

export const STATUS_HEX = Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v.hex]));

// Operational migration statuses collapse into four admin-grade buckets.
export const MIGRATION_BUCKET = {
  queued: "In progress", running: "In progress", scanning: "In progress", downloading: "In progress",
  importing: "In progress", validating: "In progress", paused: "In progress",
  complete: "Imported", completed: "Imported", skipped: "Imported",
  complete_with_warnings: "Needs attention", content_pending: "Needs attention", manual_review: "Needs attention",
  failed: "Failed", cancelled: "Failed",
};
